import React, { useEffect, useMemo, useState } from "react";
import { Loader2, Send, Plus, UserCog, Phone, PhoneCall, PhoneIncoming, AlertTriangle, FileText, ChevronDown, ChevronRight, CreditCard, CheckCircle2, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import {
    Sheet,
    SheetContent,
    SheetTitle,
    SheetDescription,
} from "@/components/ui/sheet";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
} from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { adminApi, PIPELINE_STAGES, ADVISOR_VISIBLE_STAGES } from "@/lib/adminApi";
import StatusPill from "./StatusPill";
import { useAuth } from "@/lib/auth";
import { useDialer } from "@/components/dialer/DialerProvider";

const GOLD = "#C9A84C";
const OUTCOME_LABEL = {
    answered_interested: "Answered · Interested",
    callback_requested: "Callback Requested",
    voicemail_left: "Voicemail Left",
    no_answer: "No Answer",
    policy_closed: "Policy Closed",
    not_interested: "Not Interested",
    wrong_number: "Wrong Number",
};
const OUTCOME_PILL = {
    answered_interested: "bg-emerald-100 text-emerald-800 border-emerald-200",
    callback_requested: "bg-amber-100 text-amber-900 border-amber-200",
    voicemail_left: "bg-sky-100 text-sky-900 border-sky-200",
    no_answer: "bg-zinc-100 text-zinc-700 border-zinc-200",
    policy_closed: "bg-[#C9A84C33] text-[#5A4615] border-[#C9A84C88]",
    not_interested: "bg-rose-100 text-rose-700 border-rose-200",
    wrong_number: "bg-zinc-100 text-zinc-700 border-zinc-200",
};

const centsToMoney = (c) =>
    c == null ? "—" : `$${(Number(c) / 100).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

// Defensive date renderers — never throw on null/undefined/garbage. Legacy
// call-log rows created before Phase 5.5 may have missing timestamps.
const fmtDateTime = (v) => {
    if (!v) return "—";
    const d = new Date(v);
    return isNaN(d.getTime()) ? "—" : d.toLocaleString();
};
const fmtDate = (v) => {
    if (!v) return "—";
    const d = new Date(v);
    return isNaN(d.getTime()) ? String(v) : d.toLocaleDateString();
};

export default function LeadDrawer({ leadId, open, onOpenChange, onChanged }) {
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [savingStatus, setSavingStatus] = useState(false);
    const [noteText, setNoteText] = useState("");
    const [addingNote, setAddingNote] = useState(false);
    const [agents, setAgents] = useState([]);
    const [reassignOpen, setReassignOpen] = useState(false);
    const [reassignTarget, setReassignTarget] = useState("");
    const [reassignReason, setReassignReason] = useState("");
    const [reassigning, setReassigning] = useState(false);
    const [calls, setCalls] = useState([]);
    const [callsOpen, setCallsOpen] = useState(true);

    const dialer = useDialer();

    const isAdmin = ["master_admin", "admin"].includes(user?.role);

    useEffect(() => {
        if (!leadId || !open) return;
        let alive = true;
        setLoading(true);
        adminApi
            .lead(leadId)
            .then((d) => alive && setData(d))
            .catch(() => toast.error("Couldn't load lead."))
            .finally(() => alive && setLoading(false));
        return () => { alive = false; };
    }, [leadId, open]);

    useEffect(() => {
        if (!isAdmin || !open) return;
        adminApi
            .assignableOwners()
            .then((r) => setAgents(r.items || []))
            .catch(() => {});
    }, [isAdmin, open]);

    // Phase 4 — load this lead's calls + their call_logs (Phase 5)
    useEffect(() => {
        if (!leadId || !open) return;
        let alive = true;
        Promise.all([
            adminApi.calls({ lead_id: leadId, page_size: 50 }),
            adminApi.callLogs({ lead_id: leadId, page_size: 100 }).catch(() => ({ items: [] })),
        ])
            .then(([callsR, logsR]) => {
                if (!alive) return;
                const logsByCallId = new Map();
                for (const l of logsR.items || []) logsByCallId.set(l.call_id, l);
                const merged = (callsR.items || []).map((c) => ({
                    ...c,
                    call_log: logsByCallId.get(c.id) || null,
                }));
                setCalls(merged);
            })
            .catch(() => alive && setCalls([]));
        return () => { alive = false; };
    }, [leadId, open]);

    const callThisLead = () => {
        if (!lead?.phone) {
            toast.error("This lead has no phone number on file.");
            return;
        }
        dialer.placeCall({
            to: lead.phone,
            lead: { id: lead.id, name: lead.name, phone: lead.phone },
        });
    };

    const lead = data?.lead;
    const summary = data?.summary;
    const messages = data?.messages || [];
    const carriers = data?.suggested_carriers || [];
    const carrierVisibleToAdvisor =
        !lead ? false : ADVISOR_VISIBLE_STAGES.has(lead.status);
    const showCarriers = isAdmin || carrierVisibleToAdvisor;

    // Phase 5.5 — derive unique policies attached to this lead (via call logs).
    // Every policy has a call_log_id, so this captures 100% of closed policies
    // without requiring a separate backend endpoint.
    const leadPolicies = useMemo(() => {
        const seen = new Set();
        const out = [];
        for (const c of calls) {
            const pol = c.call_log?.policy;
            if (!pol || !pol.id || seen.has(pol.id)) continue;
            seen.add(pol.id);
            out.push(pol);
        }
        // Newest first so freshly closed policies surface at the top.
        return out.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
    }, [calls]);

    const refetchCallsAndLogs = () => {
        if (!lead?.id) return;
        Promise.all([
            adminApi.calls({ lead_id: lead.id, page_size: 50 }),
            adminApi.callLogs({ lead_id: lead.id, page_size: 100 }).catch(() => ({ items: [] })),
        ])
            .then(([cR, lR]) => {
                const mp = new Map();
                for (const l of lR.items || []) mp.set(l.call_id, l);
                setCalls((cR.items || []).map((c) => ({ ...c, call_log: mp.get(c.id) || null })));
            })
            .catch(() => {});
    };

    const availableStages = useMemo(() => {
        if (isAdmin) return PIPELINE_STAGES;
        if (!lead) return [];
        // Advisor: only allow Sent to Brent -> Closed transition.
        if (lead.status === "Sent to Brent") return ["Sent to Brent", "Closed"];
        return [lead.status]; // no-op
    }, [isAdmin, lead]);

    const changeStatus = async (newStatus) => {
        if (!lead || newStatus === lead.status) return;
        setSavingStatus(true);
        try {
            const res = await adminApi.patchLead(lead.id, { status: newStatus });
            setData(res);
            toast.success(`Status → ${newStatus}`);
            onChanged?.();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't change status.");
        } finally {
            setSavingStatus(false);
        }
    };

    const addNote = async () => {
        if (!noteText.trim() || !lead) return;
        setAddingNote(true);
        try {
            const res = await adminApi.patchLead(lead.id, { notes_add: noteText.trim() });
            setData(res);
            setNoteText("");
            toast.success("Note added.");
            onChanged?.();
        } catch {
            toast.error("Couldn't add note.");
        } finally {
            setAddingNote(false);
        }
    };

    const sendToBrent = async () => {
        if (!lead) return;
        if (!window.confirm("Send this lead to Brent? This records an audit entry and drops a handoff email into the outbox.")) return;
        await changeStatus("Sent to Brent");
    };

    const submitReassign = async () => {
        if (!lead || !reassignTarget || !reassignReason.trim()) return;
        setReassigning(true);
        try {
            const updated = await adminApi.assignLead(lead.id, {
                user_id: reassignTarget,
                reason: reassignReason.trim(),
            });
            setData((d) => ({ ...d, lead: updated }));
            setReassignOpen(false);
            setReassignTarget("");
            setReassignReason("");
            toast.success("Lead reassigned.");
            onChanged?.();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't reassign.");
        } finally {
            setReassigning(false);
        }
    };

    const agentLabel = (uid) => {
        const a = agents.find((x) => x.id === uid);
        if (!a) return uid ? uid.slice(0, 8) : "—";
        return [a.first_name, a.last_name].filter(Boolean).join(" ") || a.email;
    };

    const canSendToBrent =
        isAdmin &&
        lead &&
        ["Approved", "Contact Ready", "Scheduled"].includes(lead.status);

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent
                side="right"
                className="w-full bg-white sm:max-w-2xl p-0 overflow-y-auto"
                data-testid="lead-drawer"
            >
                {loading || !lead ? (
                    <div className="flex h-full items-center justify-center">
                        <Loader2 className="animate-spin text-mm-navy" />
                    </div>
                ) : (
                    <div>
                        <div className="sticky top-0 z-10 border-b border-mm-line bg-white px-6 py-4">
                            <SheetTitle className="mm-display text-2xl text-mm-navy">
                                {lead.name || "Unnamed lead"}
                            </SheetTitle>
                            <SheetDescription className="mt-1 flex items-center gap-2 text-mm-muted">
                                <StatusPill status={lead.status} />
                                {lead.hot_flag && (
                                    <span className="inline-flex items-center rounded-full border border-red-200 bg-red-50 px-2 py-0.5 text-[11px] font-medium text-red-800">
                                        HOT · {lead.score}/100
                                    </span>
                                )}
                                {lead.user_id && (
                                    <span
                                        className="inline-flex items-center gap-1.5 rounded-full border border-mm-sky/30 bg-mm-sky/10 px-2 py-0.5 text-[11px] font-medium text-mm-blue"
                                        data-testid="lead-google-badge"
                                        title="This prospect signed in with Google"
                                    >
                                        {lead.profile_picture_url ? (
                                            <img
                                                src={lead.profile_picture_url}
                                                alt=""
                                                className="h-3.5 w-3.5 rounded-full object-cover"
                                                referrerPolicy="no-referrer"
                                            />
                                        ) : null}
                                        Signed in via Google
                                    </span>
                                )}
                                <span className="ml-auto text-xs">ID: {lead.id.slice(0, 8)}</span>
                            </SheetDescription>
                        </div>

                        <div className="px-6 py-5 space-y-6">
                            {/* Identity */}
                            <section data-testid="lead-identity">
                                <div className="mm-eyebrow mb-2">Identity</div>
                                <div className="grid grid-cols-2 gap-3 text-sm">
                                    <Field label="Email">{lead.email || "—"}</Field>
                                    <Field label="Phone">
                                        <div className="flex items-center gap-1.5">
                                            <span>{lead.phone || "—"}</span>
                                            {lead.phone && (
                                                <button
                                                    onClick={callThisLead}
                                                    className="rounded-full p-1 text-emerald-700 hover:bg-emerald-50"
                                                    title={`Call ${lead.phone}`}
                                                    data-testid="lead-drawer-call-button"
                                                >
                                                    <Phone size={11} />
                                                </button>
                                            )}
                                        </div>
                                    </Field>
                                    <Field label="Age">
                                        {lead.age != null ? `${lead.age}` : (lead.age_range || "—")}
                                    </Field>
                                    <Field label="State">{lead.state || "—"}</Field>
                                    <Field label="DOB">{lead.dob || "—"}</Field>
                                    <Field label="Sex">{lead.sex || "—"}</Field>
                                    <Field label="Tobacco">
                                        {lead.tobacco === true ? "Yes" : lead.tobacco === false ? "No" : "—"}
                                    </Field>
                                    <Field label="Source">{lead.source || "—"}</Field>
                                </div>
                            </section>

                            {/* Coverage */}
                            <section data-testid="lead-coverage">
                                <div className="mm-eyebrow mb-2">Coverage</div>
                                <div className="grid grid-cols-2 gap-3 text-sm">
                                    <Field label="Product">{lead.product_interest || "—"}</Field>
                                    <Field label="Budget">{lead.budget || "—"}</Field>
                                    <Field label="Urgency">{lead.urgency || "—"}</Field>
                                    <Field label="Current coverage">{lead.coverage_status || "—"}</Field>
                                </div>
                            </section>

                            {/* Phase 10 — IUL Profile: only renders when at least one IUL field is populated */}
                            {(lead.iul_financial_goal
                                || (Array.isArray(lead.iul_current_investments) && lead.iul_current_investments.length > 0)
                                || lead.iul_monthly_savings
                                || lead.iul_funding_ability) && (
                                <IulProfileSection lead={lead} />
                            )}

                            {/* Tabs: Summary / Transcript / Carrier / History */}
                            <Tabs defaultValue="summary" data-testid="lead-tabs">
                                <TabsList>
                                    <TabsTrigger value="summary" data-testid="tab-summary">Summary</TabsTrigger>
                                    <TabsTrigger value="questionnaire" data-testid="tab-questionnaire" disabled={!lead.questionnaire}>
                                        Questionnaire
                                    </TabsTrigger>
                                    <TabsTrigger value="transcript" data-testid="tab-transcript" disabled={!messages.length}>
                                        Transcript {messages.length ? `(${messages.length})` : ""}
                                    </TabsTrigger>
                                    <TabsTrigger value="carrier" data-testid="tab-carrier" disabled={!showCarriers}>
                                        Carrier fit
                                    </TabsTrigger>
                                    <TabsTrigger value="history" data-testid="tab-history">History</TabsTrigger>
                                </TabsList>

                                <TabsContent value="summary" className="space-y-3 text-sm">
                                    {summary ? (
                                        <>
                                            <p className="text-mm-ink leading-relaxed">
                                                {summary.summary_text || summary.summary}
                                            </p>
                                            <div className="grid grid-cols-2 gap-2 pt-2">
                                                <Field label="Classification">
                                                    {summary.product_classification || "—"}
                                                </Field>
                                                <Field label="Score">
                                                    {summary.lead_score ?? lead.score}/100
                                                </Field>
                                            </div>
                                        </>
                                    ) : (
                                        <p className="text-mm-muted">No Jarvis summary attached to this lead.</p>
                                    )}
                                </TabsContent>

                                <TabsContent value="questionnaire" data-testid="questionnaire-panel">
                                    {lead.questionnaire ? (
                                        <QuestionnaireView q={lead.questionnaire} lead={lead} />
                                    ) : (
                                        <p className="text-mm-muted text-sm">
                                            No questionnaire for this lead. (Organic web leads only.)
                                        </p>
                                    )}
                                </TabsContent>

                                <TabsContent value="transcript">
                                    <div className="space-y-2">
                                        {messages.map((m) => (
                                            <div
                                                key={m.id}
                                                className={`rounded-xl border px-3 py-2 text-sm ${
                                                    m.role === "user"
                                                        ? "border-mm-navy/10 bg-mm-navy/[0.03]"
                                                        : "border-mm-line bg-white"
                                                }`}
                                            >
                                                <div className="mm-eyebrow mb-0.5 !text-[9px]">
                                                    {m.role === "user" ? "Prospect" : "Jarvis"}
                                                </div>
                                                <div className="whitespace-pre-wrap">{m.content}</div>
                                            </div>
                                        ))}
                                    </div>
                                </TabsContent>

                                <TabsContent value="carrier">
                                    {showCarriers ? (
                                        <div>
                                            <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                                                Internal only — never shown to the prospect.
                                            </div>
                                            <ol className="mt-3 space-y-1.5 text-sm" data-testid="carrier-fit-list">
                                                {carriers.length === 0 ? (
                                                    <li className="text-mm-muted">
                                                        No ranking — product not classified yet.
                                                    </li>
                                                ) : (
                                                    carriers.map((c, i) => (
                                                        <li
                                                            key={c}
                                                            className="flex items-center justify-between rounded-lg border border-mm-line bg-white px-3 py-1.5"
                                                        >
                                                            <span>
                                                                <span className="mr-2 text-mm-muted tabular-nums">
                                                                    {i + 1}
                                                                </span>
                                                                {c}
                                                            </span>
                                                        </li>
                                                    ))
                                                )}
                                            </ol>
                                        </div>
                                    ) : (
                                        <p className="text-mm-muted">
                                            Carrier fit becomes visible once the lead reaches Approved.
                                        </p>
                                    )}
                                </TabsContent>

                                <TabsContent value="history">
                                    <div className="space-y-1.5 text-sm">
                                        {(lead.status_history || []).length === 0 ? (
                                            <p className="text-mm-muted">No status changes yet.</p>
                                        ) : (
                                            (lead.status_history || []).map((h, i) => (
                                                <div
                                                    key={i}
                                                    className="rounded-lg border border-mm-line bg-white px-3 py-2"
                                                >
                                                    <span className="text-mm-muted">{h.from || "—"}</span>
                                                    <span className="mx-2">→</span>
                                                    <span className="font-medium text-mm-navy">{h.to}</span>
                                                    <span className="ml-3 text-xs text-mm-muted">
                                                        by {h.by_email || h.by} ·{" "}
                                                        {new Date(h.at).toLocaleString()}
                                                    </span>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                </TabsContent>
                            </Tabs>

                            {/* Status changer + Send to Brent */}
                            <section className="mm-card space-y-3 p-4" data-testid="status-changer">
                                <div className="flex items-center gap-3">
                                    <div className="flex-1">
                                        <Label>Change status</Label>
                                        <Select
                                            value={lead.status}
                                            onValueChange={changeStatus}
                                            disabled={savingStatus || availableStages.length <= 1}
                                        >
                                            <SelectTrigger className="mt-1" data-testid="status-changer-trigger">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {availableStages.map((s) => (
                                                    <SelectItem key={s} value={s}>{s}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    {canSendToBrent && (
                                        <button
                                            onClick={sendToBrent}
                                            disabled={savingStatus}
                                            className="mm-btn-primary shrink-0 !py-2.5"
                                            data-testid="send-to-brent"
                                        >
                                            <Send size={14} />
                                            Send to Brent
                                        </button>
                                    )}
                                </div>
                                <p className="mm-disclaimer">
                                    Nothing advances automatically. Each change writes to the audit trail.
                                </p>
                            </section>

                            {/* Phase 2 — Assignment */}
                            <section className="mm-card space-y-3 p-4" data-testid="lead-assignment">
                                <div className="flex items-center justify-between gap-3">
                                    <div>
                                        <Label>Assignment</Label>
                                        <div className="mt-1 flex items-center gap-2 text-sm text-mm-ink">
                                            <UserCog size={14} className="text-mm-navy" />
                                            {lead.assigned_to_user_id ? (
                                                <>
                                                    <span className="font-medium">
                                                        {agentLabel(lead.assigned_to_user_id)}
                                                    </span>
                                                    <span className="text-xs text-mm-muted font-mono">
                                                        {lead.assigned_to_email}
                                                    </span>
                                                </>
                                            ) : (
                                                <span className="rounded-full border border-mm-navy/40 px-2 py-0.5 text-[10px] font-medium text-mm-navy">
                                                    Unassigned
                                                </span>
                                            )}
                                        </div>
                                        {lead.assigned_at && (
                                            <div className="mt-0.5 text-[11px] text-mm-muted">
                                                {lead.assignment_status === "manual" ? "Manually" : "Auto"}-assigned{" "}
                                                {new Date(lead.assigned_at).toLocaleString()}
                                                {lead.cross_state_assignment && " · cross-state"}
                                            </div>
                                        )}
                                    </div>
                                    {isAdmin && (
                                        <button
                                            onClick={() => {
                                                setReassignOpen((o) => !o);
                                                setReassignTarget(lead.assigned_to_user_id || "");
                                            }}
                                            className="mm-btn-outline shrink-0 !py-2 text-xs"
                                            data-testid="lead-reassign-toggle"
                                        >
                                            <UserCog size={12} />
                                            Reassign
                                        </button>
                                    )}
                                </div>

                                {isAdmin && reassignOpen && (
                                    <div className="space-y-2 rounded-xl border border-mm-line bg-mm-bg/40 p-3" data-testid="lead-reassign-form">
                                        <div>
                                            <Label>New assignee</Label>
                                            <Select
                                                value={reassignTarget || ""}
                                                onValueChange={setReassignTarget}
                                            >
                                                <SelectTrigger className="mt-1" data-testid="lead-reassign-target">
                                                    <SelectValue placeholder="Choose an owner" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {agents.length === 0 ? (
                                                        <SelectItem value="__none__" disabled>
                                                            No assignable owners
                                                        </SelectItem>
                                                    ) : (
                                                        agents.map((a) => {
                                                            const roleLabel =
                                                                a.role === "master_admin"
                                                                    ? "Master Admin"
                                                                    : a.role === "admin"
                                                                        ? "Admin"
                                                                        : "Agent";
                                                            const roleClass =
                                                                a.role === "master_admin"
                                                                    ? "bg-mm-navy text-white"
                                                                    : a.role === "admin"
                                                                        ? "bg-amber-100 text-amber-900"
                                                                        : "bg-emerald-100 text-emerald-900";
                                                            return (
                                                                <SelectItem key={a.id} value={a.id}>
                                                                    <span className="inline-flex items-center gap-2">
                                                                        <span>
                                                                            {[a.first_name, a.last_name].filter(Boolean).join(" ") || a.email}
                                                                        </span>
                                                                        <span
                                                                            className={`inline-flex items-center rounded-full px-2 py-0.5 text-[0.65rem] font-medium ${roleClass}`}
                                                                            data-testid={`assignable-badge-${a.id}`}
                                                                        >
                                                                            {roleLabel}
                                                                        </span>
                                                                        {(a.states_licensed || []).length > 0 && (
                                                                            <span className="text-xs text-mm-muted">
                                                                                · {(a.states_licensed || []).join(", ")}
                                                                            </span>
                                                                        )}
                                                                    </span>
                                                                </SelectItem>
                                                            );
                                                        })
                                                    )}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div>
                                            <Label>Reason (required)</Label>
                                            <Textarea
                                                value={reassignReason}
                                                onChange={(e) => setReassignReason(e.target.value.slice(0, 500))}
                                                placeholder="e.g., FL territory rebalance, agent on vacation, expertise match"
                                                rows={2}
                                                data-testid="lead-reassign-reason"
                                            />
                                            <div className="mt-0.5 text-[10px] text-mm-muted text-right">
                                                {reassignReason.length}/500
                                            </div>
                                        </div>
                                        <button
                                            onClick={submitReassign}
                                            disabled={
                                                reassigning ||
                                                !reassignTarget ||
                                                !reassignReason.trim() ||
                                                reassignTarget === lead.assigned_to_user_id
                                            }
                                            className="mm-btn-primary w-full !py-2 text-xs disabled:opacity-60"
                                            data-testid="lead-reassign-submit"
                                        >
                                            {reassigning ? (
                                                <><Loader2 size={12} className="animate-spin" /> Reassigning…</>
                                            ) : (
                                                <>Confirm reassign</>
                                            )}
                                        </button>
                                    </div>
                                )}

                                {(lead.reassignment_history || []).length > 0 && (
                                    <details className="rounded-lg bg-white" data-testid="lead-reassign-history">
                                        <summary className="cursor-pointer text-xs font-medium uppercase tracking-wider text-mm-muted">
                                            Reassignment history ({(lead.reassignment_history || []).length})
                                        </summary>
                                        <ul className="mt-2 space-y-1.5 text-xs">
                                            {(lead.reassignment_history || []).map((h, i) => (
                                                <li key={h.id || i} className="rounded border border-mm-line bg-white px-2 py-1.5">
                                                    <span className="text-mm-muted">{agentLabel(h.from_user_id)}</span>
                                                    <span className="mx-1.5">→</span>
                                                    <span className="font-medium text-mm-navy">{agentLabel(h.to_user_id)}</span>
                                                    <span className="ml-2 text-mm-muted">· {h.reason}</span>
                                                    <div className="text-[10px] text-mm-muted">
                                                        by {h.by_user_id?.slice(0, 8) || "system"} · {new Date(h.at).toLocaleString()}
                                                    </div>
                                                </li>
                                            ))}
                                        </ul>
                                    </details>
                                )}
                            </section>

                            {/* Phase 5.5 — Policies section (shows "Make a Payment" + "Mark Paid" per policy, independent of call-log expansion) */}
                            {leadPolicies.length > 0 && (
                                <section className="mm-card space-y-2 p-4" data-testid="lead-policies-section">
                                    <div className="mm-eyebrow flex items-center gap-1.5" style={{ color: GOLD }}>
                                        <FileText size={12} /> Policies ({leadPolicies.length})
                                    </div>
                                    <ul className="space-y-2">
                                        {leadPolicies.map((p) => (
                                            <LeadPolicyRow
                                                key={p.id}
                                                policy={p}
                                                onChanged={refetchCallsAndLogs}
                                            />
                                        ))}
                                    </ul>
                                </section>
                            )}

                            {/* Phase 4 — Calls history */}
                            <section className="mm-card space-y-2 p-4" data-testid="lead-calls-history">
                                <button
                                    onClick={() => setCallsOpen((o) => !o)}
                                    className="flex w-full items-center justify-between text-left"
                                >
                                    <div className="mm-eyebrow">Calls ({calls.length})</div>
                                    <span className="text-xs text-mm-muted">{callsOpen ? "−" : "+"}</span>
                                </button>
                                {callsOpen && (
                                    calls.length === 0 ? (
                                        <div className="text-xs text-mm-muted">No calls yet.</div>
                                    ) : (
                                        <ul className="space-y-2">
                                            {calls.map((c) => (
                                                <CallRow
                                                    key={c.id}
                                                    call={c}
                                                    leadId={lead.id}
                                                    onLogged={() => {
                                                        // refetch calls + logs after retroactive log
                                                        Promise.all([
                                                            adminApi.calls({ lead_id: lead.id, page_size: 50 }),
                                                            adminApi.callLogs({ lead_id: lead.id, page_size: 100 }).catch(() => ({ items: [] })),
                                                        ]).then(([cR, lR]) => {
                                                            const mp = new Map();
                                                            for (const l of lR.items || []) mp.set(l.call_id, l);
                                                            setCalls((cR.items || []).map((c) => ({ ...c, call_log: mp.get(c.id) || null })));
                                                        }).catch(() => {});
                                                    }}
                                                />
                                            ))}
                                        </ul>
                                    )
                                )}
                            </section>

                            {/* Notes */}
                            <section data-testid="lead-notes">
                                <div className="mm-eyebrow mb-2">Notes</div>
                                <div className="space-y-2">
                                    {(lead.notes || []).length === 0 ? (
                                        <p className="text-sm text-mm-muted">No notes yet.</p>
                                    ) : (
                                        (lead.notes || []).map((n) => (
                                            <div
                                                key={n.id}
                                                className="rounded-lg border border-mm-line bg-white px-3 py-2 text-sm"
                                                data-testid={`lead-note-${n.id}`}
                                            >
                                                <div className="text-xs text-mm-muted">
                                                    {n.author_email} · {new Date(n.at).toLocaleString()}
                                                </div>
                                                <div className="mt-0.5 whitespace-pre-wrap text-mm-ink">
                                                    {n.text}
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </div>
                                <div className="mt-3 space-y-2">
                                    <Textarea
                                        value={noteText}
                                        onChange={(e) => setNoteText(e.target.value)}
                                        placeholder="Add a note for the record…"
                                        rows={2}
                                        data-testid="lead-note-input"
                                    />
                                    <button
                                        onClick={addNote}
                                        disabled={!noteText.trim() || addingNote}
                                        className="mm-btn-outline !py-2 text-sm disabled:opacity-60"
                                        data-testid="lead-note-add"
                                    >
                                        {addingNote ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
                                        Add note
                                    </button>
                                </div>
                            </section>
                        </div>
                    </div>
                )}
            </SheetContent>
        </Sheet>
    );
}

function Field({ label, children }) {
    return (
        <div>
            <div className="text-[10px] font-medium uppercase tracking-wider text-mm-muted">
                {label}
            </div>
            <div className="text-mm-ink">{children}</div>
        </div>
    );
}

function Label({ children }) {
    return (
        <div className="text-[10px] font-medium uppercase tracking-wider text-mm-muted">
            {children}
        </div>
    );
}

const STEP_LABELS = {
    purpose: "Step 1 · Purpose",
    context: "Step 2 · Context",
    budget_band: "Step 3 · Coverage amount",
    urgency: "Step 4 · Timeline",
    age: "Age (slider)",
    age_range: "Age range (legacy)",
    health: "Step 6 · Health self-assessment",
    notes: "Step 8 · Note to advisor",
    product_interest: "Derived product interest",
    timezone: "Timezone",
    iul_financial_goal: "IUL · Financial goal",
    iul_current_investments: "IUL · Current investments",
    iul_monthly_savings: "IUL · Monthly savings",
    iul_funding_ability: "IUL · Funding ability",
};

// Phase 10 — human labels for the IUL enum keys captured by the wizard tiles.
const IUL_LABELS = {
    // financial goal
    grow_long_term_cash_value: "Grow Long Term Cash Value",
    borrow_against_policy: "Borrow Against My Policy",
    protect_family: "Protect My Family",
    retirement_income: "Retirement Income",
    // current investments
    k401: "401(k)",
    ira: "IRA",
    cash_savings: "Cash Savings",
    active_trading: "Active Trading",
    self_directed_brokerage: "Self Directed Brokerage Account",
    // monthly savings
    lt_300: "Less than $300",
    "300_500": "$300 – $500",
    "500_1000": "$500 – $1,000",
    gt_1000: "More than $1,000",
    // funding ability
    can_fund: "Yes, I can fund a plan",
    seeking_aid: "No, I'm looking for aid",
};
const iulLabel = (v) => (v == null ? "—" : (IUL_LABELS[v] || String(v)));

function IulProfileSection({ lead }) {
    const invs = Array.isArray(lead.iul_current_investments) ? lead.iul_current_investments : [];
    return (
        <section className="mm-card p-4" data-testid="lead-iul-profile" style={{ borderColor: "#C9A84C88" }}>
            <div className="mm-eyebrow mb-2 flex items-center gap-1.5" style={{ color: "#8A6B1E" }}>
                <TrendingUp size={12} /> IUL Profile
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm">
                <Field label="Financial goal">{iulLabel(lead.iul_financial_goal)}</Field>
                <Field label="Monthly savings">{iulLabel(lead.iul_monthly_savings)}</Field>
                <Field label="Funding ability">
                    <span
                        className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium border ${
                            lead.iul_funding_ability === "can_fund"
                                ? "bg-emerald-50 text-emerald-800 border-emerald-200"
                                : lead.iul_funding_ability === "seeking_aid"
                                    ? "bg-amber-50 text-amber-900 border-amber-200"
                                    : "bg-mm-bg text-mm-muted border-mm-line"
                        }`}
                        data-testid="lead-iul-funding-pill"
                    >
                        {iulLabel(lead.iul_funding_ability)}
                    </span>
                </Field>
                <Field label="Current investments">
                    {invs.length === 0
                        ? "—"
                        : (
                            <div className="flex flex-wrap gap-1">
                                {invs.map((v) => (
                                    <span key={v} className="rounded-full border border-mm-line bg-white px-2 py-0.5 text-[11px] text-mm-ink">
                                        {iulLabel(v)}
                                    </span>
                                ))}
                            </div>
                        )
                    }
                </Field>
            </div>
        </section>
    );
}

function _fmt(value) {
    if (value === null || value === undefined || value === "") return "—";
    if (Array.isArray(value)) return value.join(", ");
    if (typeof value === "boolean") return value ? "Yes" : "No";
    return String(value);
}

function QuestionnaireView({ q, lead }) {
    const rows = [
        ["purpose", q.purpose],
        ["context", q.context],
        ["budget_band", q.budget_band],
        ["urgency", q.urgency],
        ["age", q.age],
        ["age_range", q.age_range],
        ["health", q.health],
        ["iul_financial_goal", q.iul_financial_goal],
        ["iul_current_investments", q.iul_current_investments],
        ["iul_monthly_savings", q.iul_monthly_savings],
        ["iul_funding_ability", q.iul_funding_ability],
        ["product_interest", q.product_interest],
        ["notes", q.notes],
        ["timezone", q.timezone],
    ];
    const IUL_KEYS = new Set(["iul_financial_goal", "iul_current_investments", "iul_monthly_savings", "iul_funding_ability"]);
    const visibleRows = rows.filter(([k, v]) => {
        if (v === null || v === undefined || v === "") return false;
        if (Array.isArray(v) && v.length === 0) return false;
        return true;
    });
    return (
        <div className="space-y-4">
            <div className="rounded-xl border border-mm-line bg-mm-bg/60 p-3 text-xs text-mm-muted">
                Answers captured from the homepage 8-step wizard.
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
                {visibleRows.map(([k, v]) => (
                    <div key={k} className="rounded-lg border border-mm-line bg-white px-3 py-2">
                        <div className="text-[10px] font-medium uppercase tracking-wider text-mm-muted">
                            {STEP_LABELS[k] || k}
                        </div>
                        <div className="mt-0.5 text-sm text-mm-ink break-words">
                            {IUL_KEYS.has(k)
                                ? (Array.isArray(v) ? v.map(iulLabel).join(", ") : iulLabel(v))
                                : _fmt(v)}
                        </div>
                    </div>
                ))}
            </div>
            <div className="mt-4 rounded-xl border border-mm-line bg-white p-3">
                <div className="mm-eyebrow mb-2">Step 7 · Contact preferences</div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                    <Field label="Best method">{lead.contact_method || "—"}</Field>
                    <Field label="Best time">{lead.contact_time || "—"}</Field>
                    <Field label="Zip">{lead.zip || "—"}</Field>
                    <Field label="State">{lead.state || "—"}</Field>
                </div>
            </div>
        </div>
    );
}

// -------------------- Phase 5 — CallRow --------------------

function CallRow({ call, leadId, onLogged }) {
    const [expanded, setExpanded] = useState(false);
    const [logging, setLogging] = useState(false);
    const [detail, setDetail] = useState(null);
    const [loadingDetail, setLoadingDetail] = useState(false);

    const log = call.call_log;
    const isClosed = log?.outcome === "policy_closed";
    const annualPremium = log?.policy?.annual_premium;

    const durationStr = call.duration_seconds
        ? `${Math.floor(call.duration_seconds / 60)}:${String(call.duration_seconds % 60).padStart(2, "0")}`
        : "—";

    const rowBg = isClosed ? { backgroundColor: `${GOLD}1F`, borderColor: `${GOLD}88` } : {};
    const rowText = isClosed ? { color: "#5A4615" } : {};

    const toggleExpand = async () => {
        const next = !expanded;
        setExpanded(next);
        if (next && log?.id && !detail && !loadingDetail) {
            setLoadingDetail(true);
            try {
                const d = await adminApi.callLog(log.id);
                setDetail(d);
            } catch {
                /* ignore */
            } finally {
                setLoadingDetail(false);
            }
        }
    };

    const logNow = async (e) => {
        e.stopPropagation();
        // Minimal retroactive log — outcome blank means agent will set it next time
        // they look at it; for now we drop "no_answer" as a safe default that keeps
        // the "Log missing" badge from showing on rows that genuinely lack data.
        if (!window.confirm("Log this call as 'No Answer' for now? You can edit the outcome later.")) return;
        setLogging(true);
        try {
            await adminApi.createCallLog({
                call_id: call.id,
                outcome: "no_answer",
                notes: "",
            });
            toast.success("Call logged.");
            onLogged?.();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't log call.");
        } finally {
            setLogging(false);
        }
    };

    return (
        <li
            className="rounded-lg border p-2.5"
            style={{ ...rowBg, borderColor: isClosed ? `${GOLD}88` : undefined, ...(isClosed ? {} : {}) }}
            data-testid={`lead-call-row-${call.id}`}
        >
            <div className="flex items-center justify-between gap-2 text-xs">
                <div className="flex items-center gap-2" style={rowText}>
                    {call.direction === "outbound" ? (
                        <PhoneCall size={12} className={isClosed ? "" : "text-mm-navy"} />
                    ) : (
                        <PhoneIncoming size={12} className={isClosed ? "" : "text-emerald-700"} />
                    )}
                    <span className="font-medium">
                        {call.agent_email?.split("@")[0] || "—"}
                    </span>
                    <span className={isClosed ? "opacity-70" : "text-mm-muted"}>
                        {fmtDateTime(call?.started_at || call?.created_at)}
                    </span>
                </div>
                <div className="flex items-center gap-2">
                    <span className="tabular-nums" style={rowText}>
                        {durationStr}
                    </span>
                    <span
                        className={[
                            "rounded-full px-1.5 py-0.5 text-[9px] font-medium",
                            call.status === "completed" ? "bg-emerald-100 text-emerald-800"
                                : call.trial_blocked ? "bg-amber-100 text-amber-900"
                                : ["failed", "no-answer", "busy"].includes(call.status) ? "bg-rose-100 text-rose-700"
                                : "bg-mm-bg text-mm-muted",
                        ].join(" ")}
                    >
                        {call.trial_blocked ? "trial blocked" : call.status}
                    </span>
                </div>
            </div>

            <div className="mt-1.5 flex items-center justify-between gap-2">
                {log ? (
                    <button
                        onClick={toggleExpand}
                        className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium ${OUTCOME_PILL[log.outcome] || "bg-mm-bg text-mm-muted border-mm-line"}`}
                        data-testid={`call-outcome-pill-${call.id}`}
                    >
                        {expanded ? <ChevronDown size={9} /> : <ChevronRight size={9} />}
                        {OUTCOME_LABEL[log.outcome] || log.outcome}
                    </button>
                ) : call.status === "completed" || call.status === "no-answer" || call.status === "busy" || call.status === "failed" ? (
                    <div className="flex items-center gap-2">
                        <span
                            className="inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-900"
                            data-testid={`call-log-missing-${call.id}`}
                        >
                            <AlertTriangle size={9} /> Log missing
                        </span>
                        <button
                            onClick={logNow}
                            disabled={logging}
                            className="rounded-full border border-mm-line bg-white px-2 py-0.5 text-[10px] font-medium text-mm-navy hover:bg-mm-bg disabled:opacity-50"
                            data-testid={`call-log-now-${call.id}`}
                        >
                            {logging ? "Logging…" : "Log now"}
                        </button>
                    </div>
                ) : null}
                {isClosed && annualPremium ? (
                    <span
                        className="rounded-full px-2 py-0.5 text-[10px] font-semibold"
                        style={{ backgroundColor: GOLD, color: "white" }}
                        data-testid={`call-premium-badge-${call.id}`}
                    >
                        💰 {centsToMoney(annualPremium)} premium
                    </span>
                ) : null}
            </div>

            {expanded && log && (
                <div
                    className="mt-2 space-y-1.5 rounded-md border border-mm-line bg-white p-2 text-[11px]"
                    data-testid={`call-log-detail-${call.id}`}
                >
                    {loadingDetail ? (
                        <Loader2 size={12} className="animate-spin" />
                    ) : (
                        <>
                            <DetailRow k="Submitted by" v={(detail || log)?.submitted_by?.slice(0, 8) || "—"} />
                            <DetailRow k="Call date" v={(detail || log)?.call_date || "—"} />
                            <DetailRow k="Start" v={fmtDateTime((detail || log)?.call_start_at)} />
                            <DetailRow k="End" v={fmtDateTime((detail || log)?.call_end_at)} />
                            {(detail || log)?.notes && (
                                <div className="mt-1">
                                    <div className="text-[9px] font-medium uppercase tracking-wider text-mm-muted">Notes</div>
                                    <div className="whitespace-pre-wrap text-mm-ink">
                                        {(detail || log)?.notes}
                                    </div>
                                </div>
                            )}
                            {isClosed && (detail || log)?.policy && (
                                <div className="mt-2 rounded bg-[#C9A84C1A] p-1.5">
                                    <div className="flex items-center gap-1 text-[9px] font-medium uppercase tracking-wider" style={{ color: GOLD }}>
                                        <FileText size={9} /> Policy
                                    </div>
                                    <DetailRow k="Carrier" v={(detail || log)?.policy?.carrier || "—"} />
                                    <DetailRow k="Type" v={(detail || log)?.policy?.policy_type || "—"} />
                                    <DetailRow k="Coverage" v={centsToMoney((detail || log)?.policy?.coverage_amount)} />
                                    <DetailRow k="Monthly" v={centsToMoney((detail || log)?.policy?.monthly_premium)} />
                                    <DetailRow k="Annual" v={centsToMoney((detail || log)?.policy?.annual_premium)} />
                                    <DetailRow k="Effective" v={(detail || log)?.policy?.effective_date || "—"} />
                                    <DetailRow k="Policy #" v={(detail || log)?.policy?.policy_number || "—"} />
                                    <div className="mt-1 text-[9px] italic text-mm-muted">
                                        Payment actions are surfaced in the Policies section above.
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}

            {call.has_recording && (
                <audio
                    controls
                    preload="none"
                    src={adminApi.callRecordingUrl(call.id)}
                    className="mt-2 h-8 w-full"
                    data-testid={`lead-call-audio-${call.id}`}
                />
            )}
            {call.call_notes && (
                <div className="mt-1.5 text-[11px] whitespace-pre-wrap" style={rowText}>
                    {call.call_notes}
                </div>
            )}
        </li>
    );
}

function DetailRow({ k, v }) {
    return (
        <div className="flex items-center justify-between gap-2">
            <span className="text-[9px] uppercase tracking-wider text-mm-muted">{k}</span>
            <span className="text-mm-ink text-right">{v}</span>
        </div>
    );
}


const PAYMENT_STATUS_PILL = {
    pending: "bg-zinc-100 text-zinc-700 border-zinc-200",
    paid: "bg-emerald-100 text-emerald-800 border-emerald-200",
    missed: "bg-rose-100 text-rose-700 border-rose-200",
    lapsed: "bg-rose-50 text-rose-700 border-rose-300 border-dashed",
};
const PAYMENT_STATUS_LABEL = {
    pending: "Pending",
    paid: "Paid",
    missed: "Missed",
    lapsed: "Lapsed",
};

function PaymentActions({ policyId, onChanged }) {
    const [policy, setPolicy] = useState(null);
    const [loading, setLoading] = useState(true);
    const [marking, setMarking] = useState(false);
    const [linking, setLinking] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const p = await adminApi.policy(policyId);
                if (!cancelled) setPolicy(p);
            } catch {
                /* ignore */
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [policyId]);

    if (loading) {
        return <div className="mt-1.5 text-[10px] text-mm-muted">Loading payment info…</div>;
    }
    if (!policy) return null;

    const status = policy.payment_status || "pending";
    const nextDraft = policy.next_draft_date || null;

    const openPortal = async () => {
        setLinking(true);
        try {
            const res = await adminApi.policyPayLink(policyId);
            if (!res.portal_url) {
                toast.error(res.message || "Carrier portal not configured.");
                return;
            }
            window.open(res.portal_url, "_blank", "noopener,noreferrer");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't open carrier portal.");
        } finally {
            setLinking(false);
        }
    };

    const markPaid = async () => {
        setMarking(true);
        try {
            const updated = await adminApi.markPolicyPaid(policyId);
            setPolicy(updated);
            toast.success("Marked paid. Next draft advanced.");
            onChanged?.();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't mark paid.");
        } finally {
            setMarking(false);
        }
    };

    return (
        <div className="mt-2 border-t border-dashed border-[#C9A84C88] pt-2 space-y-1.5" data-testid={`payment-actions-${policyId}`}>
            <div className="flex items-center justify-between">
                <span className="text-[9px] uppercase tracking-wider text-mm-muted">Payment status</span>
                <span
                    className={`rounded-full border px-1.5 py-0.5 text-[9px] font-medium ${PAYMENT_STATUS_PILL[status]}`}
                    data-testid={`payment-status-pill-${policyId}`}
                >
                    {PAYMENT_STATUS_LABEL[status] || status}
                </span>
            </div>
            <DetailRow k="Next Draft" v={nextDraft || "Not set"} />
            <DetailRow k="Frequency" v={policy.draft_frequency || "monthly"} />
            {policy.last_paid_confirmed_at && (
                <DetailRow k="Last paid" v={fmtDate(policy.last_paid_confirmed_at)} />
            )}
            <div className="flex items-center gap-1.5 pt-1">
                <button
                    onClick={openPortal}
                    disabled={linking}
                    title={`Opens ${policy.carrier || "carrier"} payment portal in a new tab. Moving Mountains does not process payments.`}
                    className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold text-white disabled:opacity-60"
                    style={{ backgroundColor: GOLD }}
                    data-testid={`policy-pay-link-${policyId}`}
                >
                    <CreditCard size={10} /> {linking ? "Opening…" : "Make a Payment"}
                </button>
                <button
                    onClick={markPaid}
                    disabled={marking || status === "paid"}
                    className="inline-flex items-center gap-1 rounded-full border border-emerald-300 bg-emerald-50 px-2 py-0.5 text-[10px] font-medium text-emerald-800 hover:bg-emerald-100 disabled:opacity-50"
                    data-testid={`policy-mark-paid-${policyId}`}
                >
                    <CheckCircle2 size={10} /> {marking ? "Saving…" : status === "paid" ? "Paid" : "Mark Paid"}
                </button>
            </div>
            <div className="pt-0.5 text-[9px] italic text-mm-muted">
                Moving Mountains does not collect or process payments. We redirect you to the carrier.
            </div>
        </div>
    );
}

function LeadPolicyRow({ policy, onChanged }) {
    return (
        <li
            className="rounded-lg border p-2.5"
            style={{ backgroundColor: `${GOLD}1F`, borderColor: `${GOLD}88` }}
            data-testid={`lead-policy-row-${policy.id}`}
        >
            <div className="flex items-center justify-between gap-2 text-xs" style={{ color: "#5A4615" }}>
                <div className="flex items-center gap-2 min-w-0">
                    <FileText size={12} style={{ color: GOLD }} />
                    <span className="font-semibold truncate">
                        {policy.carrier || "Carrier"} · {policy.policy_type || "—"}
                    </span>
                </div>
                <div className="flex items-center gap-2 shrink-0 tabular-nums">
                    <span>{centsToMoney(policy.coverage_amount)} coverage</span>
                    {policy.annual_premium ? (
                        <span
                            className="rounded-full px-1.5 py-0.5 text-[9px] font-semibold"
                            style={{ backgroundColor: GOLD, color: "white" }}
                        >
                            {centsToMoney(policy.annual_premium)}/yr
                        </span>
                    ) : null}
                </div>
            </div>
            <PaymentActions policyId={policy.id} onChanged={onChanged} />
        </li>
    );
}

